import React, { useEffect, useState } from 'react';

import Spinner from 'component/Layout/Loader/Spinner';

import classNames from 'classnames';

import { QaIdentifier, useQa } from 'hooks/useQa';

import { RelationType } from 'types/relation';

import { Theme } from './config';
import { CtaTarget, CtaTheme, CtaType } from './types';

import styles from './Cta.module.scss';

export interface CtaProps {
    /** What kind of CTA do you want, they have the same appearance but different behaviour */
    type?: CtaType;
    /** Choose the appearance */
    theme?: CtaTheme;
    /** Is the CTA active? */
    isActive?: boolean;
    /** Works only with type link */
    link?: string;
    /** Defines the target of the link */
    linkTarget?: CtaTarget;
    /** Should a loader be visible on click? */
    withLoader?: boolean;
    /** Custom classes for customization */
    customClassList?: {
        Spinner?: string;
        Cta?: string;
    };
    /** optional clickHandler */
    onClick?: (event: React.SyntheticEvent) => void;
    /** reset currentState */
    isIdle?: boolean;
    /** defines the rel attribute of the link **/
    relation?: RelationType;
    id?: string;
    qaId?: QaIdentifier;
    hasPreventDefault?: boolean;
}

const getQaId = (theme: CtaTheme): QaIdentifier | undefined => {
    switch (theme) {
        case CtaTheme.PRIMARY:
            return QaIdentifier.Cta_Primary;
        case CtaTheme.SECONDARY:
            return QaIdentifier.Cta_Secondary;
        case CtaTheme.TERTIARY:
            return QaIdentifier.Cta_Tertiary;
        case CtaTheme.INVERTED:
            return QaIdentifier.Cta_Inverted;
        default:
            return undefined;
    }
};

const Cta: React.FC<React.PropsWithChildren<CtaProps>> = ({
    type = CtaType.BUTTON,
    theme = CtaTheme.PRIMARY,
    isActive = true,
    children,
    link = '',
    linkTarget,
    withLoader = false,
    customClassList,
    onClick,
    isIdle = true,
    relation,
    id,
    qaId,
    hasPreventDefault = false,
}) => {
    const baseStyles = classNames(
        styles.Cta,
        Theme[theme].stylingClass,
        {
            [styles.Inactive]: !isActive,
        },
        customClassList?.Cta
    );
    const [ctaClass, setCtaClass] = useState('');
    const qaAttr = useQa(qaId ?? getQaId(theme));
    const handleOnClick = (e: React.SyntheticEvent) => {
        if (isActive) {
            // cta children should bubble events
            if (e.currentTarget === e.target && hasPreventDefault) {
                e.preventDefault();
            }
            theme === CtaTheme.PRIMARY && setCtaClass(styles.LoaderVisible);
            onClick?.(e);
        }
    };
    useEffect(() => {
        if (isIdle) {
            setCtaClass('');
        }
    }, [isIdle]);

    switch (type) {
        case CtaType.LINK:
            if (link) {
                return (
                    <a
                        rel={relation}
                        href={link}
                        onClick={handleOnClick}
                        target={linkTarget}
                        className={`${baseStyles} ${ctaClass}`}
                        id={id}
                        {...qaAttr}
                    >
                        <span>{children}</span>
                    </a>
                );
            }
            return null;
        case CtaType.BUTTON:
            return (
                <button
                    onClick={handleOnClick}
                    onKeyDown={(e) => e.key === 'Enter' && handleOnClick(e)}
                    className={`${baseStyles} ${ctaClass}`}
                    id={id}
                    {...qaAttr}
                >
                    {children}
                    {withLoader && (
                        <Spinner
                            type={'bold'}
                            classListObject={{
                                Spinner: classNames(
                                    customClassList?.Spinner,
                                    styles.Loader
                                ),
                            }}
                        />
                    )}
                </button>
            );

        default:
            return null;
    }
};
export default Cta;
